import React, { useMemo } from 'react';
import { ExternalLink } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useTranslation } from '@/components/utils/translations';
import { getConfidenceBadge } from '@/components/accounts/ProfileSectionDisplay';
import {
  FINANCIAL_FOOTNOTE_METRIC,
  formatFinancialMetricValue,
  getOrderedFinancialMetrics,
  parseKeyAssetTableRows,
} from '@/lib/accountFinancials';

function FinancialValueCell({ entry }) {
  const formatted = formatFinancialMetricValue(entry);

  const valueNode = formatted.tooltip ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="font-medium text-[#0A1A2F] cursor-default">{formatted.display}</span>
      </TooltipTrigger>
      <TooltipContent side="top">{formatted.tooltip}</TooltipContent>
    </Tooltip>
  ) : (
    <span className="font-medium text-[#0A1A2F]">{formatted.display}</span>
  );

  return (
    <div>
      {valueNode}
      {entry?.source ? (
        <p className="text-xs text-[#5C6B7A]/90 mt-1.5 leading-relaxed">{entry.source}</p>
      ) : null}
    </div>
  );
}

function FinancialMetricLabel({ metricKey, url }) {
  return (
    <div className="flex items-start gap-1.5">
      <span className="font-medium text-sm text-[#0A1A2F]">{metricKey}</span>
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#00C2C2] hover:text-[#00A3A3] shrink-0 mt-0.5"
          aria-label={`Source link for ${metricKey}`}
        >
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      ) : null}
    </div>
  );
}

function MetricsTable({ rows, t }) {
  if (!rows.length) return null;

  return (
    <>
      <div className="hidden md:block border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="text-xs font-medium text-left w-[28%]">Metric</TableHead>
              <TableHead className="text-xs font-medium text-left w-[42%]">Value</TableHead>
              <TableHead className="text-xs font-medium text-left w-[18%]">Period</TableHead>
              <TableHead className="text-xs font-medium text-center w-20">Confidence</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((entry) => (
              <TableRow
                key={entry.key}
                className={`hover:bg-gray-50 align-top ${entry._rowClass || ''}`}
              >
                <TableCell className="align-top py-3">
                  <FinancialMetricLabel metricKey={entry.key} url={entry.url} />
                </TableCell>
                <TableCell className="text-sm align-top py-3">
                  {entry._plainValue ? (
                    <span className="text-[#0A1A2F] leading-relaxed">{entry._plainValue}</span>
                  ) : (
                    <FinancialValueCell entry={entry} />
                  )}
                </TableCell>
                <TableCell className="text-sm text-[#5C6B7A] align-top py-3">
                  {entry.period || '—'}
                </TableCell>
                <TableCell className="text-center align-top py-3">
                  {entry.confidence ? getConfidenceBadge(entry.confidence, t) : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="md:hidden space-y-3">
        {rows.map((entry) => (
          <Card key={entry.key} className={`border border-gray-200 ${entry._rowClass || ''}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex justify-between items-start gap-2">
                <FinancialMetricLabel metricKey={entry.key} url={entry.url} />
                {entry.confidence ? getConfidenceBadge(entry.confidence, t) : null}
              </div>
              {entry._plainValue ? (
                <p className="text-sm text-[#0A1A2F] leading-relaxed">{entry._plainValue}</p>
              ) : (
                <FinancialValueCell entry={entry} />
              )}
              {entry.period ? (
                <p className="text-xs text-[#5C6B7A]">Period: {entry.period}</p>
              ) : null}
            </CardContent>
          </Card>
        ))}
      </div>
    </>
  );
}

export default function FinancialsTab({ account, financialData }) {
  const { t } = useTranslation();
  const allMetrics = getOrderedFinancialMetrics(financialData ?? account?.financial_data);
  const tableMetrics = allMetrics.filter((entry) => !entry._isFootnote);
  const footnoteMetric = allMetrics.find((entry) => entry.key === FINANCIAL_FOOTNOTE_METRIC);
  const keyAssetRows = useMemo(
    () =>
      parseKeyAssetTableRows(account?.key_assets).map((row) => ({
        key: row.label,
        _plainValue: row.value,
        _rowClass: row.isWatchItem ? 'bg-amber-50/60' : '',
      })),
    [account?.key_assets],
  );
  const hasMetrics = tableMetrics.length > 0 || footnoteMetric;
  const hasKeyAssets = keyAssetRows.length > 0;
  const hasEmployees = account?.employees != null;

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-8">
        {hasEmployees && (
          <p className="text-sm text-[#5C6B7A]">
            <span className="font-medium text-[#0A1A2F]">Employees:</span>{' '}
            {Number(account.employees).toLocaleString('en-US')}
          </p>
        )}

        {hasMetrics && (
          <div>
            <MetricsTable rows={tableMetrics} t={t} />

            {footnoteMetric ? (
              <div className="mt-4 pt-3 border-t border-gray-100">
                <p className="text-xs font-medium text-[#5C6B7A] mb-1">{footnoteMetric.key}</p>
                <div className="text-xs text-[#5C6B7A]/90 leading-relaxed">
                  <FinancialValueCell entry={footnoteMetric} />
                </div>
              </div>
            ) : null}

            <p className="text-xs text-[#5C6B7A] mt-4 leading-relaxed">
              Public-intelligence data — confidence reflects source quality, not verification by NEO GTM.
            </p>
          </div>
        )}

        {hasKeyAssets && (
          <section>
            <h3 className="text-sm font-semibold text-[#0A1A2F] mb-3">Key assets</h3>
            <MetricsTable rows={keyAssetRows} t={t} />
          </section>
        )}
      </div>
    </TooltipProvider>
  );
}
